/**
 * Configuración regional fijada del cliente: español de Chile y zona de Chile continental
 * (criterios 26.1 a 26.3).
 *
 * Los formateadores se construyen **una vez** y se reutilizan. No es una optimización
 * cosmética: construir un `Intl.DateTimeFormat` es caro, y una lista de 90 días con fecha y
 * hora por fila construiría 180 formateadores si cada celda armara el suyo.
 *
 * Ninguno de los cuatro depende del reloj ni del huso del navegador. Un teléfono con la zona
 * mal puesta, o una persona conectada desde otro país, ve exactamente las mismas horas que
 * quien está en Santiago, que es lo que el criterio 26.3 pide: los instantes se guardan en
 * UTC y se presentan en la zona de Chile.
 *
 * La hora se fija con `hourCycle: 'h23'` y no con `hour12: false`: en español de Chile la
 * segunda forma presenta la medianoche como «24:00», que no es lo que nadie espera leer.
 */

import { IDIOMA_CHILE, ZONA_HORARIA_CHILE } from '@aisalud/shared/types';

/** Etiqueta de idioma de toda la interfaz, también la que se declara en `Accept-Language`. */
export const IDIOMA = IDIOMA_CHILE;

/** Zona en la que se presentan todos los instantes. */
export const ZONA = ZONA_HORARIA_CHILE;

const FECHA = new Intl.DateTimeFormat(IDIOMA, {
  timeZone: ZONA,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const HORA = new Intl.DateTimeFormat(IDIOMA, {
  timeZone: ZONA,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const FECHA_Y_HORA = new Intl.DateTimeFormat(IDIOMA, {
  timeZone: ZONA,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/**
 * Fecha civil ya resuelta (`YYYY-MM-DD`). Se presenta en UTC **a propósito**: el valor ya es
 * un día del calendario chileno, así que pasarlo por la zona lo movería un día hacia atrás.
 */
const FECHA_CIVIL = new Intl.DateTimeFormat(IDIOMA, {
  timeZone: 'UTC',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const NUMERO = new Intl.NumberFormat(IDIOMA);

/**
 * Convierte el instante canónico —segundos desde la época, UTC— en la fecha de JavaScript,
 * que cuenta en milisegundos.
 *
 * La tarea 2.1 reemplaza el `number` por el alias `UtcInstant` del paquete compartido, y
 * entonces el compilador impide pasar milisegundos donde van segundos.
 */
function desdeInstante(instanteUtc: number): Date {
  return new Date(instanteUtc * 1000);
}

/** Fecha de un instante, como día, mes y año (criterio 26.2). */
export function formatearFecha(instanteUtc: number): string {
  return FECHA.format(desdeInstante(instanteUtc));
}

/** Hora de un instante, en formato de 24 horas (criterio 26.2). */
export function formatearHora(instanteUtc: number): string {
  return HORA.format(desdeInstante(instanteUtc));
}

/** Fecha y hora de un instante, en la zona de Chile continental. */
export function formatearFechaHora(instanteUtc: number): string {
  return FECHA_Y_HORA.format(desdeInstante(instanteUtc));
}

/**
 * Fecha civil `YYYY-MM-DD` como día, mes y año.
 *
 * Se interpreta a mediodía UTC y no a medianoche por prudencia: cualquier corrimiento de
 * horas en el camino dejaría el resultado dentro del mismo día.
 */
export function formatearFechaCivil(fechaCivil: string): string {
  return FECHA_CIVIL.format(new Date(`${fechaCivil}T12:00:00Z`));
}

/** Número con el separador decimal y de miles de Chile. */
export function formatearNumero(valor: number): string {
  return NUMERO.format(valor);
}
